// Cross-repo contract with zl-api `packages/features/src/referral/referralData.ts`: the cookie name
// and every field name in it. Change both together — a rename fails nothing, it just stops
// attribution arriving. `tests/referralData.test.ts` pins both halves on this side.

import { getCookie, setCookie } from './cookie';

/**
 * Where acquisition data lives, and why it moved.
 *
 * `user.referralData` is written once at signup and never updated, so what matters is what the
 * visitor arrived with — possibly many pages and one origin ago. This was kept in `localStorage`,
 * which is per-origin: the educators app on its own subdomain could not see any of it, and every
 * account created there was attributed to nothing at all.
 *
 * A cookie on the parent domain is visible to both. That is not a new idea — `zlDeviceId` already
 * works exactly this way — and the shape here is shared with `@zl/features/referral` in the zl-api
 * monorepo. **The name and the field names are a contract between the two repos.** Rename one side
 * and nothing fails to compile; attribution simply stops arriving.
 */
export const REFERRAL_COOKIE = 'zlReferralData';

/** Where this used to live. Read to migrate, then deleted, and never written again. */
const LEGACY_STORAGE_KEY = 'referralData';

export interface ReferralData {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmHabitat?: string;
  partner?: string;
  referrer?: string;
  /** Only this app can produce one; the educators app preserves it untouched. */
  gaClientID?: string;
}

const parse = (raw: string | undefined | null): ReferralData | undefined => {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : undefined;
  } catch {
    // Corrupt or hand-edited. Attribution is never worth failing a page load over.
    return undefined;
  }
};

export const writeReferralData = (data: ReferralData) => {
  setCookie(REFERRAL_COOKIE, JSON.stringify(data));
};

const readLegacy = (): ReferralData | undefined => {
  try {
    return parse(localStorage.getItem(LEGACY_STORAGE_KEY));
  } catch {
    // Storage disabled. Same answer as empty.
    return undefined;
  }
};

/**
 * What we know about how this visitor arrived.
 *
 * The cookie is the store; the `localStorage` this used to live in is checked on **every** read
 * until it is gone. Returning early on a present cookie would have left the old key untouched
 * forever on the one browser that has both — a visitor whose cookie was written on the educators
 * side while their original attribution still sat here — and quietly lost what was in it.
 *
 * ## Which wins where they disagree
 *
 * The legacy copy. This is first-touch attribution: `user.referralData` records how someone was
 * *acquired*, so the older answer is the right one, and the legacy key is older by construction —
 * nothing has written it since this shipped. A visitor who came from an Instagram ad long ago and
 * has since followed a campaign link into the educators app is still an Instagram signup.
 *
 * That is the same rule the merge in `logAndGetCampaignData` has always applied, where a stored
 * value beats the one on the current URL.
 *
 * ## Why the removal is guarded
 *
 * Only once the cookie has been written and read back *as what was written*. A browser refusing
 * cookies would otherwise have its attribution deleted and not stored; instead it re-migrates on
 * every read, which costs a read and is the right way to fail.
 *
 * Comparing the value matters, not just finding one. On the browser this is actually for — one that
 * already holds a cookie and still has the legacy key — a refused write leaves the *old* cookie
 * sitting there, so merely asking whether a cookie exists answers yes and deletes the first-touch
 * fields that the write was supposed to preserve.
 */
export const readReferralData = (): ReferralData => {
  const fromCookie = parse(getCookie(REFERRAL_COOKIE)) ?? {};
  const legacy = readLegacy();

  if (!legacy) {
    return fromCookie;
  }

  // Empty values never override — an absent field in the old copy must not blank out a real one.
  const merged: ReferralData = { ...fromCookie };
  Object.entries(legacy).forEach(([key, value]) => {
    if (value) {
      merged[key as keyof ReferralData] = value;
    }
  });

  writeReferralData(merged);
  if (getCookie(REFERRAL_COOKIE) === JSON.stringify(merged)) {
    try {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      // Nothing to do; the cookie is the source of truth from here regardless.
    }
  }
  return merged;
};
